import { DocumentOnView } from 'presentation/document/documentOnView';
import { EXTENSIONS } from 'shared/constants/EXTENSIONS';
import { AttachmentsScope, ImageDisplayStyle } from '../../../types/model/common';
import {
  FileMetadataOptions,
  SectionEstimateConfig,
} from '../../buildReportPageEstimateInput';
import { PdfDocumentCounts } from '../../classifyDocumentsForPdfEstimate';
import {
  isPdfSupportedDocument,
  isPdfSupportedImage,
} from '../classifyDocumentOnViewsForPdfEstimate';
import {
  INSPECTION_PDF_LABELS,
  INSPECTION_PDF_SAMPLE_VALUES,
} from './inspectionPdfLabels';
import {
  isRenderablePdfBlock,
  PdfBlock,
  pdfColumn,
  PdfContent,
  pdfFixedBlock,
  pdfNextPage,
  pdfRichText,
  pdfSinglePageColumn,
  pdfText,
} from './pdf/pdfBlocks';
import {
  availableRatioInCell,
  gridSpanWidth,
  PDF_MAX_FULL_SIZE_IMAGE_HEIGHT,
} from './pdf/pdfPage';
import { regularFont } from './pdf/pdfTextMetrics';
import { greyCaptionHtml, greyHtml } from './inspectionPdfTypography';

/** What the PDF prints for one attachment: a picture or a list entry, with its metadata lines. */
export type PdfAttachment = {
  isImage: boolean;
  title: string;
  description: string;
  extension: string;
};

/** Where the block sits: the share of the page grid its cell spans and the padding drawn inside the cell. */
export type AttachmentsCellGeometry = {
  widthRatio: number;
  horizontalPadding: number;
};

export type CompactAttachmentsParams = {
  attachments: PdfAttachment[];
  config: SectionEstimateConfig & { scope?: AttachmentsScope };
  fileMetadata: FileMetadataOptions;
  label: string;
  geometry: AttachmentsCellGeometry;
};

/** `pdfConfig.maxMiniaturePictureHeight` and the image margins of a miniature (2 / 2 / 0 / 6). */
const MINIATURE_MAX_HEIGHT = 160;
const MINIATURE_IMAGE_HORIZONTAL_MARGIN = 4;
const MINIATURE_IMAGE_BOTTOM_MARGIN = 6;
const MINIATURES_PER_ROW = 3;

const LABEL_MARGIN = { top: 8, bottom: 2 };
const METADATA_MARGIN = { left: 4, top: 2 };
const LIST_ENTRY_MARGIN = { top: 2 };
const LIST_MARGIN = { top: 8 };
const MINIATURE_ROW_MARGIN = 4;
const FULL_SIZE_METADATA_MARGIN = 8;

export function documentOnViewToPdfAttachment(document: DocumentOnView): PdfAttachment {
  return {
    isImage: isPdfSupportedImage(document),
    title: document.title ?? '',
    description: document.description ?? '',
    extension: document.data.extension ?? '',
  };
}

export function documentOnViewsToPdfAttachments(
  documents: DocumentOnView[] | undefined
): PdfAttachment[] {
  return (documents ?? [])
    .filter(isPdfSupportedDocument)
    .map(documentOnViewToPdfAttachment);
}

const DESCRIPTION_FILLER = 'kontrola zbrojenia ściany fundamentowej zgodność z projektem ';

function fillerText(length: number): string {
  return DESCRIPTION_FILLER.repeat(Math.ceil(length / DESCRIPTION_FILLER.length))
    .slice(0, length)
    .trim();
}

/**
 * Issue rows carry attachment counts only, so the entries are synthesised: typical file names and the
 * description characters spread evenly over the files.
 */
export function documentCountsToPdfAttachments(counts: PdfDocumentCounts): PdfAttachment[] {
  const files = counts.images + counts.nonImages;
  const description = fillerText(files > 0 ? Math.round(counts.descriptionChars / files) : 0);
  const entry = (isImage: boolean): PdfAttachment => ({
    isImage,
    title: INSPECTION_PDF_SAMPLE_VALUES.imageFileName,
    description,
    extension: isImage ? EXTENSIONS.JPG : EXTENSIONS.PDF,
  });
  return [
    ...Array.from({ length: counts.images }, () => entry(true)),
    ...Array.from({ length: counts.nonImages }, () => entry(false)),
  ];
}

/** `FilterDocumentsForPdf`: the main image of an issue is its first picture. */
class AttachmentSelection {
  constructor(private readonly params: CompactAttachmentsParams) {}

  private get isMainOnly(): boolean {
    return this.params.config.scope === AttachmentsScope.main;
  }

  private get isList(): boolean {
    return this.params.config.displayStyle === ImageDisplayStyle.list;
  }

  private get mainImage(): PdfAttachment[] {
    return this.params.attachments.filter((attachment) => attachment.isImage).slice(0, 1);
  }

  get pictures(): PdfAttachment[] {
    if (this.isList) {
      return [];
    }
    return this.isMainOnly
      ? this.mainImage
      : this.params.attachments.filter((attachment) => attachment.isImage);
  }

  get listed(): PdfAttachment[] {
    if (this.isList) {
      return this.isMainOnly ? this.mainImage : this.params.attachments;
    }
    return this.isMainOnly
      ? []
      : this.params.attachments.filter((attachment) => !attachment.isImage);
  }
}

/** `PdfItemOutDtoFactory.sectionDocuments` with `compactAttachments` and a combined section label. */
class CompactAttachmentsBlockBuilder {
  private readonly availableRatio: number;
  private readonly showMetadata: boolean;

  constructor(
    private readonly params: CompactAttachmentsParams,
    private readonly contentWidth: number
  ) {
    this.availableRatio = availableRatioInCell(
      params.geometry.widthRatio,
      params.geometry.horizontalPadding
    );
    const { showFileNames, showFileDescriptions, showActiveLinks } = params.fileMetadata;
    this.showMetadata = showFileNames || showFileDescriptions || showActiveLinks;
  }

  build(): PdfContent {
    if (!this.params.config.enabled) {
      return undefined;
    }
    const selection = new AttachmentSelection(this.params);
    const content = [
      this.pictures(selection.pictures),
      this.list(selection.listed),
    ].filter(isRenderablePdfBlock);
    if (content.length === 0) {
      return undefined;
    }
    return pdfColumn([
      pdfText(this.params.label, this.contentWidth, { margin: LABEL_MARGIN }),
      ...content,
    ]);
  }

  private pictures(pictures: PdfAttachment[]): PdfContent {
    if (pictures.length === 0) {
      return undefined;
    }
    if (this.params.config.displayStyle === ImageDisplayStyle.fullPage) {
      return pdfSinglePageColumn(pictures.map((picture) => this.fullSizePicture(picture)));
    }
    const miniatures = this.miniatureItems(pictures);
    return pdfColumn([
      pdfSinglePageColumn(miniatures.slice(0, MINIATURES_PER_ROW)),
      ...miniatures.slice(MINIATURES_PER_ROW),
    ]);
  }

  private fullSizePicture(picture: PdfAttachment): PdfBlock {
    const width = gridSpanWidth(this.availableRatio);
    const metadata = this.showMetadata ? this.metadataItems(picture, width) : [];
    return pdfSinglePageColumn([
      pdfFixedBlock(
        Math.min(
          width * INSPECTION_PDF_SAMPLE_VALUES.imageAspectRatio,
          PDF_MAX_FULL_SIZE_IMAGE_HEIGHT
        )
      ),
      metadata.length > 0
        ? pdfColumn(metadata, { top: FULL_SIZE_METADATA_MARGIN })
        : undefined,
      pdfNextPage(),
    ]);
  }

  private miniatureItems(pictures: PdfAttachment[]): PdfBlock[] {
    if (pictures.length === 1 && this.showMetadata) {
      return [
        this.miniatureRow([this.miniature(pictures[0], false)]),
        ...this.metadataItems(pictures[0], this.contentWidth),
      ];
    }
    const rows: PdfBlock[] = [];
    for (let index = 0; index < pictures.length; index += MINIATURES_PER_ROW) {
      rows.push(
        this.miniatureRow(
          pictures
            .slice(index, index + MINIATURES_PER_ROW)
            .map((picture) => this.miniature(picture, this.showMetadata))
        )
      );
    }
    return rows;
  }

  /** A grid row: drawn whole, as tall as its tallest miniature. */
  private miniatureRow(miniatures: PdfBlock[]): PdfBlock {
    return pdfFixedBlock(
      MINIATURE_ROW_MARGIN + Math.max(...miniatures.map((miniature) => miniature.height))
    );
  }

  private miniature(picture: PdfAttachment, withMetadata: boolean): PdfBlock {
    const columnWidth = gridSpanWidth(this.availableRatio / MINIATURES_PER_ROW);
    const imageHeight = Math.min(
      (columnWidth - MINIATURE_IMAGE_HORIZONTAL_MARGIN) *
        INSPECTION_PDF_SAMPLE_VALUES.imageAspectRatio,
      MINIATURE_MAX_HEIGHT - MINIATURE_IMAGE_BOTTOM_MARGIN
    );
    return pdfColumn([
      pdfFixedBlock(imageHeight + MINIATURE_IMAGE_BOTTOM_MARGIN),
      ...(withMetadata ? this.metadataItems(picture, columnWidth) : []),
    ]);
  }

  /** `createFileMetadataItems` in compact mode: `Nazwa pliku: …` / link, then `Opis: …`. */
  private metadataItems(attachment: PdfAttachment, width: number): PdfBlock[] {
    const { showFileNames, showFileDescriptions, showActiveLinks } = this.params.fileMetadata;
    const items: PdfBlock[] = [];
    if (showFileNames) {
      items.push(this.inlineMetadata(INSPECTION_PDF_LABELS.fileName, attachment.title, width));
    } else if (showActiveLinks) {
      items.push(
        pdfColumn([
          pdfText(INSPECTION_PDF_LABELS.downloadLink, width, {
            font: regularFont(8),
            margin: { left: 4 },
          }),
          pdfText(INSPECTION_PDF_LABELS.downloadLink, width, { margin: { left: 4 } }),
        ])
      );
    }
    if (showFileDescriptions && this.hasOwnDescription(attachment)) {
      items.push(
        this.inlineMetadata(INSPECTION_PDF_LABELS.fileDescription, attachment.description, width)
      );
    }
    return items;
  }

  private hasOwnDescription(attachment: PdfAttachment): boolean {
    return attachment.description.length > 0 && attachment.description !== attachment.title;
  }

  private inlineMetadata(label: string, value: string, width: number): PdfBlock {
    return pdfRichText(`${greyCaptionHtml(label)} ${value}`, width, { margin: METADATA_MARGIN });
  }

  private list(attachments: PdfAttachment[]): PdfContent {
    if (attachments.length === 0) {
      return undefined;
    }
    const { showFileNames, showFileDescriptions } = this.params.fileMetadata;
    const entries = attachments.flatMap((attachment, index) => {
      const position = index + 1;
      const name = showFileNames
        ? attachment.title
        : INSPECTION_PDF_LABELS.filePlaceholder(position, attachment.extension);
      const extension = attachment.extension.toUpperCase();
      const extensionHtml = extension ? `${greyHtml(extension)} ` : '';
      return [
        pdfRichText(`${position}. ${extensionHtml}${name}`, this.contentWidth, {
          margin: LIST_ENTRY_MARGIN,
        }),
        showFileDescriptions && this.hasOwnDescription(attachment)
          ? this.inlineMetadata(
              INSPECTION_PDF_LABELS.fileDescription,
              attachment.description,
              this.contentWidth
            )
          : undefined,
      ];
    });
    return pdfSinglePageColumn([pdfColumn(entries, LIST_MARGIN)]);
  }
}

/** Compact attachments block of an inspection report cell, sized for `contentWidth` inside the cell. */
export function compactAttachmentsBlock(
  params: CompactAttachmentsParams,
  contentWidth: number
): PdfContent {
  return new CompactAttachmentsBlockBuilder(params, contentWidth).build();
}
